/**
 * Ambient particles drawn in front of the scene (behind the UI): pollen,
 * leaves, bubbles, sparkles, petals, snow, butterflies, feathers, confetti
 * and waterfall spray.
 *
 * The system is stateless: each particle's position is a closed-form function
 * of (seed, index, time). Nothing accumulates, so a paused game resumes
 * exactly where it stopped, reduced motion is simply time 0 (a static
 * scatter), and the maths is unit-testable without a canvas.
 */
import type { ParticleKind, ThemeParticles } from '@dropstack/core';
import { clamp, wrap, type SceneSize } from './layout.ts';
import { hash01 } from './noise.ts';
import { circlePath, ellipsePath, leafPath, pick, rgba, shade, twinkle, type Ctx } from './painters/draw.ts';

/** Where one particle is at a moment, in CSS px. */
export interface ParticleState {
  x: number;
  y: number;
  /** Rotation (radians). */
  angle: number;
  /** Size multiplier (≈ 0.7–1.3). */
  scale: number;
  /** 0–1 opacity. */
  alpha: number;
  /** Kind-specific cycle value: wing beat (butterflies), flip (confetti, petals). */
  cycle: number;
  /** 0–1: picks a tint variation. */
  tone: number;
}

interface Motion {
  /** Vertical speed, scene units per second (negative rises). */
  fall: number;
  /** Side-to-side sway amplitude (units) and rate (rad/s). */
  sway: number;
  swayRate: number;
  /** Steady sideways drift (units/s). */
  drift: number;
  /** Base size (units). */
  size: number;
  /** Rotation speed (rad/s). */
  spin: number;
}

/** Every kind drifts slowly: the whole screen takes a minute or more to cross. */
const MOTION: Record<ParticleKind, Motion> = {
  pollen: { fall: -0.007, sway: 0.02, swayRate: 0.5, drift: 0.004, size: 0.006, spin: 0 },
  leaves: { fall: 0.022, sway: 0.04, swayRate: 0.8, drift: 0.006, size: 0.022, spin: 0.9 },
  bubbles: { fall: -0.02, sway: 0.012, swayRate: 1.4, drift: 0, size: 0.012, spin: 0 },
  sparkles: { fall: -0.004, sway: 0.006, swayRate: 0.4, drift: 0, size: 0.014, spin: 0 },
  petals: { fall: 0.016, sway: 0.035, swayRate: 0.9, drift: 0.01, size: 0.014, spin: 1.2 },
  snow: { fall: 0.015, sway: 0.02, swayRate: 0.6, drift: 0.003, size: 0.007, spin: 0 },
  butterflies: { fall: 0, sway: 0, swayRate: 0, drift: 0, size: 0.022, spin: 0 },
  feathers: { fall: 0.011, sway: 0.05, swayRate: 0.7, drift: 0.004, size: 0.03, spin: 0 },
  confetti: { fall: 0.03, sway: 0.02, swayRate: 1.1, drift: 0, size: 0.012, spin: 2.2 },
  spray: { fall: -0.03, sway: 0.01, swayRate: 1, drift: 0.004, size: 0.005, spin: 0 },
};

/** Festival colours mixed into confetti alongside the theme's own particle colour. */
const CONFETTI = ['#ff6b6b', '#4dabf7', '#69db7c', '#f783ac', '#9775fa', '#ffd43b'];

/**
 * Relative busyness of one particle of each kind. Big, eye-catching particles
 * (butterflies, feathers, tumbling leaves) get fewer slots than specks of
 * pollen or snow, so every theme feels about equally lively, never cluttered.
 */
const WEIGHT: Record<ParticleKind, number> = {
  pollen: 1,
  leaves: 0.6,
  bubbles: 0.8,
  sparkles: 0.9,
  petals: 0.75,
  snow: 1,
  butterflies: 0.35,
  feathers: 0.5,
  confetti: 0.9,
  spray: 1,
};

/** Particle budget: scales with screen area, so a phone and a desktop look equally busy. */
export function particleCount(kind: ParticleKind, density: number, width: number, height: number): number {
  const budget = clamp((width * height) / 7000, 14, 90);
  return Math.round(budget * WEIGHT[kind] * clamp(density, 0, 1));
}

/** Largest particle extent (px) — used as the off-screen margin particles wrap through. */
function marginFor(kind: ParticleKind, size: SceneSize): number {
  return MOTION[kind].size * size.unit * 3 + 4;
}

/** Position of particle `i` at `time` seconds. Pure and deterministic. */
export function particleState(kind: ParticleKind, i: number, seed: number, time: number, size: SceneSize): ParticleState {
  const m = MOTION[kind];
  const r = (k: number) => hash01(seed, i * 8 + k);
  const u0 = r(0);
  const v0 = r(1);
  const speed = 0.7 + 0.6 * r(2);
  const phase = r(3) * Math.PI * 2;
  const scale = 0.7 + 0.6 * r(4);
  const tone = r(5);
  const { width: W, height: H, unit } = size;
  const margin = marginFor(kind, size);

  if (kind === 'butterflies') {
    // Lazy loops around a home point in the lower two-thirds of the screen.
    const x = u0 * W + Math.sin(time * 0.23 * speed + phase) * unit * 0.12 + Math.sin(time * 0.61 + phase * 2) * unit * 0.04;
    const y = (0.35 + 0.55 * v0) * H + Math.sin(time * 0.31 * speed + phase) * unit * 0.06 + Math.sin(time * 0.9 + phase) * unit * 0.015;
    const heading = Math.cos(time * 0.23 * speed + phase) * 0.23 * speed + Math.cos(time * 0.61 + phase * 2) * 0.61 * 0.33;
    return { x, y, angle: heading >= 0 ? 0 : Math.PI, scale, alpha: 1, cycle: Math.abs(Math.sin(time * 7 * speed + phase)), tone };
  }

  if (kind === 'spray') {
    // Droplets puff up from the lower part of the screen and fade as they rise.
    const life = wrap(time * 0.07 * speed + v0, 0, 1);
    const x = u0 * W + Math.sin(time * m.swayRate + phase) * unit * m.sway + (life - 0.5) * unit * m.drift * 20;
    const y = H * (1.02 - 0.55 * life);
    return { x, y, angle: 0, scale, alpha: Math.sin(Math.PI * life) * 0.8, cycle: life, tone };
  }

  const spanY = H + margin * 2;
  const spanX = W + margin * 2;
  const y = wrap(v0 * spanY + m.fall * unit * speed * time, 0, spanY) - margin;
  const sway = Math.sin(time * m.swayRate * speed + phase) * unit * m.sway;
  const x = wrap(u0 * spanX + m.drift * unit * time + sway, 0, spanX) - margin;

  switch (kind) {
    case 'sparkles':
      return { x, y, angle: 0, scale, alpha: Math.max(0, Math.sin(time * (0.8 + speed) + phase)) ** 2, cycle: 0, tone };
    case 'feathers':
      // A feather rocks like a pendulum as it drifts down.
      return { x, y, angle: Math.sin(time * m.swayRate * speed + phase) * 0.7 + 0.3, scale, alpha: 0.9, cycle: 0, tone };
    case 'pollen':
      return { x, y, angle: 0, scale, alpha: 0.55 + 0.35 * Math.sin(time * 1.3 + phase), cycle: 0, tone };
    default:
      return { x, y, angle: phase + time * m.spin * (tone < 0.5 ? 1 : -1), scale, alpha: 0.9, cycle: Math.cos(time * m.spin * 1.3 * speed + phase), tone };
  }
}

// ───────────── drawing ─────────────

type ParticleDrawer = (ctx: Ctx, p: ParticleState, r: number, color: string, index: number) => void;

const DRAW: Record<ParticleKind, ParticleDrawer> = {
  pollen(ctx, p, r, color) {
    ctx.beginPath();
    circlePath(ctx, p.x, p.y, r * 2.6);
    ctx.fillStyle = rgba(color, p.alpha * 0.25);
    ctx.fill();
    ctx.beginPath();
    circlePath(ctx, p.x, p.y, r);
    ctx.fillStyle = rgba(color, Math.min(1, p.alpha + 0.3));
    ctx.fill();
  },
  leaves(ctx, p, r, color) {
    const tint = shade(color, p.tone * 0.3 - 0.15);
    ctx.save();
    ctx.translate(p.x, p.y);
    ctx.rotate(p.angle);
    ctx.scale(1, 0.55 + 0.45 * Math.abs(p.cycle));
    ctx.beginPath();
    leafPath(ctx, 0, 0, r * 2, r * 0.55, 0);
    ctx.fillStyle = rgba(tint, p.alpha);
    ctx.fill();
    ctx.strokeStyle = rgba(shade(tint, -0.3), 0.7);
    ctx.lineWidth = Math.max(0.75, r * 0.12);
    ctx.stroke();
    ctx.beginPath();
    ctx.moveTo(-r * 0.9, 0);
    ctx.lineTo(r * 0.9, 0);
    ctx.stroke();
    ctx.restore();
  },
  bubbles(ctx, p, r, color) {
    ctx.beginPath();
    circlePath(ctx, p.x, p.y, r);
    ctx.fillStyle = rgba(color, 0.18);
    ctx.fill();
    ctx.strokeStyle = rgba(color, 0.85);
    ctx.lineWidth = Math.max(0.75, r * 0.14);
    ctx.stroke();
    ctx.beginPath();
    ellipsePath(ctx, p.x - r * 0.35, p.y - r * 0.38, r * 0.3, r * 0.18, -0.6);
    ctx.fillStyle = 'rgba(255, 255, 255, 0.85)';
    ctx.fill();
  },
  sparkles(ctx, p, r, color) {
    twinkle(ctx, p.x, p.y, r, color, p.alpha);
    if (p.alpha > 0.3) twinkle(ctx, p.x, p.y, r * 0.35, '#ffffff', p.alpha);
  },
  petals(ctx, p, r, color) {
    ctx.save();
    ctx.translate(p.x, p.y);
    ctx.rotate(p.angle);
    ctx.scale(0.35 + 0.65 * Math.abs(p.cycle), 1);
    ctx.beginPath();
    ctx.moveTo(0, -r);
    ctx.bezierCurveTo(r * 0.9, -r * 0.6, r * 0.7, r * 0.8, 0, r);
    ctx.bezierCurveTo(-r * 0.7, r * 0.8, -r * 0.9, -r * 0.6, 0, -r);
    ctx.closePath();
    ctx.fillStyle = rgba(p.tone < 0.5 ? color : shade(color, 0.25), p.alpha);
    ctx.fill();
    ctx.strokeStyle = rgba(shade(color, -0.25), 0.5);
    ctx.lineWidth = Math.max(0.75, r * 0.1);
    ctx.stroke();
    ctx.restore();
  },
  snow(ctx, p, r, color) {
    ctx.beginPath();
    circlePath(ctx, p.x, p.y, r);
    ctx.fillStyle = rgba(color, 0.92);
    ctx.fill();
    ctx.strokeStyle = 'rgba(160, 200, 235, 0.55)';
    ctx.lineWidth = Math.max(0.75, r * 0.2);
    ctx.stroke();
    if (p.tone > 0.8) twinkle(ctx, p.x, p.y, r * 2, '#ffffff', 0.8);
  },
  butterflies(ctx, p, r, color) {
    const wing = p.tone < 0.5 ? color : shade(color, 0.25);
    const accent = shade(color, -0.3);
    const open = 0.25 + 0.75 * p.cycle;
    ctx.save();
    ctx.translate(p.x, p.y);
    if (p.angle !== 0) ctx.scale(-1, 1);
    for (const side of [-1, 1]) {
      ctx.save();
      ctx.scale(side * open, 1);
      ctx.beginPath();
      ellipsePath(ctx, r * 0.55, -r * 0.35, r * 0.62, r * 0.5, -0.5);
      ellipsePath(ctx, r * 0.42, r * 0.38, r * 0.42, r * 0.34, 0.5);
      ctx.fillStyle = wing;
      ctx.fill();
      ctx.strokeStyle = accent;
      ctx.lineWidth = Math.max(0.75, r * 0.1);
      ctx.stroke();
      ctx.beginPath();
      circlePath(ctx, r * 0.65, -r * 0.4, r * 0.16);
      ctx.fillStyle = 'rgba(255, 255, 255, 0.85)';
      ctx.fill();
      ctx.restore();
    }
    ctx.beginPath();
    ellipsePath(ctx, 0, 0, r * 0.12, r * 0.6);
    ctx.fillStyle = '#3b2f4a';
    ctx.fill();
    ctx.restore();
  },
  feathers(ctx, p, r, color) {
    ctx.save();
    ctx.translate(p.x, p.y);
    ctx.rotate(p.angle);
    ctx.beginPath();
    leafPath(ctx, 0, 0, r * 2.2, r * 0.42, -Math.PI / 2);
    ctx.fillStyle = rgba(color, p.alpha);
    ctx.fill();
    ctx.strokeStyle = rgba(shade(color, -0.3), 0.6);
    ctx.lineWidth = Math.max(0.75, r * 0.08);
    ctx.stroke();
    ctx.beginPath();
    ctx.moveTo(0, r * 1.3);
    ctx.lineTo(0, -r * 0.9);
    ctx.stroke();
    ctx.restore();
  },
  confetti(ctx, p, r, color, index) {
    ctx.save();
    ctx.translate(p.x, p.y);
    ctx.rotate(p.angle);
    ctx.scale(1, 0.2 + 0.8 * Math.abs(p.cycle));
    ctx.fillStyle = index % 3 === 0 ? color : pick(CONFETTI, index);
    ctx.fillRect(-r * 0.7, -r * 0.4, r * 1.4, r * 0.8);
    ctx.restore();
  },
  spray(ctx, p, r, color) {
    if (p.alpha <= 0.01) return;
    ctx.beginPath();
    circlePath(ctx, p.x, p.y, r * (1 + p.cycle));
    ctx.fillStyle = rgba(color, p.alpha * 0.8);
    ctx.fill();
  },
};

/** Draw the theme's particles at `time` (0 for a static frame). */
export function drawParticles(ctx: Ctx, particles: ThemeParticles, size: SceneSize, time: number, seed: number): void {
  const n = particleCount(particles.kind, particles.density, size.width, size.height);
  const base = MOTION[particles.kind].size * size.unit;
  const draw = DRAW[particles.kind];
  ctx.save();
  ctx.lineCap = 'round';
  ctx.lineJoin = 'round';
  for (let i = 0; i < n; i++) {
    const p = particleState(particles.kind, i, seed, time, size);
    draw(ctx, p, Math.max(1, base * p.scale), particles.color, i);
  }
  ctx.restore();
}
